import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { config } from "@/shared/lib/config";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
} from "@/shared/lib/utils";

const FILTER_ALL = "__all__" as const;

function toParam(v: string): string | undefined {
  return v === FILTER_ALL || !v ? undefined : v;
}

type CouponsSearch = {
  promotionId?: string;
  isActive?: string;
  search?: string;
  offset?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
};

export function CouponsFilter() {
  const nav = useNavigate({ from: "/app/loyalty/promotions/coupons" });
  const search = useSearch({
    from: "/app/loyalty/promotions/coupons",
  }) as CouponsSearch;

  const hasFilter =
    !!search.promotionId || !!search.isActive || !!search.search;

  const [searchInputValue, setSearchInputValue] = useState<string>(
    search.search ?? "",
  );
  useEffect(() => {
    setSearchInputValue(search.search ?? "");
  }, [search.search]);

  const debouncedSearch = useDebounceCallback((val: string) => {
    update({ search: val || undefined });
  }, 400);

  const update = (next: Partial<CouponsSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  const clearFilters = () => {
    setSearchInputValue("");
    nav({
      search: {
        offset: search.offset ?? 0,
        limit: search.limit ?? 20,
        sortField: search.sortField,
        sortOrder: search.sortOrder,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <Tabs
        className="w-full"
        value={
          search.isActive === "true" || search.isActive === "false"
            ? search.isActive
            : FILTER_ALL
        }
        onValueChange={(v) => update({ isActive: toParam(v ?? "") })}
      >
        <TabsList className="h-9 flex-wrap gap-1 bg-muted/50">
          <TabsTrigger value={FILTER_ALL} className="text-xs">
            ທຸກສະຖານະ
          </TabsTrigger>
          <TabsTrigger value="true" className="text-xs">
            ເປີດໃຊ້ງານ
          </TabsTrigger>
          <TabsTrigger value="false" className="text-xs">
            ປິດໃຊ້ງານ
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="w-56">
        <InfiniteCombobox<{ id: string; name: string }>
          queryKey={["promotions", "lookup", "active"]}
          queryFn={(args) =>
            fetchLookupForInfinite(
              `${config.apiUrl}/loyalty/promotions/lookup`,
              args,
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(
              `${config.apiUrl}/loyalty/promotions/lookup`,
              id,
            )
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          value={search.promotionId ?? ""}
          onValueChange={(val) => update({ promotionId: val || undefined })}
          placeholder="ເລືອກໂປຣໂມຊັນ..."
          className="h-9"
          clearable
        />
      </div>
      <Input
        type="search"
        placeholder="ຄົ້ນຫາລະຫັດຄູປອງ..."
        className="w-56"
        defaultValue={searchInputValue}
        onChange={(e) => {
          const v = e.target.value;
          setSearchInputValue(v);
          debouncedSearch(v);
        }}
      />
      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
